import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import {
  clearAuthSession,
  establishAuthSession,
  getAccessToken,
  getCachedUserData,
  logoutForSessionExpired,
  setCachedUserData,
} from "../global/utils/authSession";

export const AuthContext = createContext(null);

const authInfoUrl = () =>
  `${process.env.REACT_APP_API_URL}/api/accounts/auth/info/`;

export const AuthProvider = ({ children }) => {
  const [userData, setUserDataState] = useState(() => getCachedUserData());
  const [authLoading, setAuthLoading] = useState(() => Boolean(getAccessToken()));

  const setUserData = useCallback((data) => {
    if (!data) {
      clearAuthSession();
      setUserDataState(null);
      return;
    }
    if (data.access_token) {
      const user = establishAuthSession(data);
      setUserDataState(user);
      return;
    }
    const token = getAccessToken();
    const next = token ? { ...data, access_token: token } : { ...data };
    setCachedUserData(next);
    setUserDataState(next);
  }, []);

  const loginWithPayload = useCallback(
    (loginPayload) => {
      const user = establishAuthSession(loginPayload);
      setUserDataState(user);
      return user;
    },
    [],
  );

  const logout = useCallback((expired = false) => {
    if (expired) {
      logoutForSessionExpired();
      return;
    }
    clearAuthSession();
    setUserDataState(null);
  }, []);

  const refreshUserInfo = useCallback(async () => {
    const token = getAccessToken();
    if (!token) {
      setUserDataState(null);
      setCachedUserData(null);
      setAuthLoading(false);
      return null;
    }

    setAuthLoading(true);
    try {
      const response = await axios.get(authInfoUrl(), {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const payload = response?.data?.data ?? response?.data;
      if (!payload || typeof payload !== "object") {
        throw new Error("Invalid auth info response.");
      }
      const user = { ...payload, access_token: token };
      setCachedUserData(user);
      setUserDataState(user);
      return user;
    } catch (error) {
      const status = error?.response?.status;
      if ([401, 403].includes(status)) {
        logoutForSessionExpired();
        return null;
      }
      clearAuthSession();
      setUserDataState(null);
      throw error;
    } finally {
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = getAccessToken();
    if (!token) {
      setAuthLoading(false);
      return;
    }
    refreshUserInfo().catch(() => {
      /* handled above */
    });
  }, [refreshUserInfo]);

  const value = useMemo(
    () => ({
      userData,
      setUserData,
      loginWithPayload,
      logout,
      refreshUserInfo,
      authLoading,
      isAuthenticated: Boolean(userData?.access_token || getAccessToken()),
    }),
    [userData, setUserData, loginWithPayload, logout, refreshUserInfo, authLoading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthData = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthData must be used within an AuthProvider");
  }
  return context;
};
